import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Heart,
  MessageCircle,
  Award,
  Scale,
  Sparkles,
  Tag,
  ArrowLeft,
  ChevronRight,
  Play,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingWhatsApp from "../components/FloatingWhatsApp";
import DesignCard from "../components/DesignCard";
import {
  designUrl,
} from "../data/catalog";
import { useCatalog } from "../context/CatalogContext";
import { useWishlist } from "../context/WishlistContext";
import { useStoreSettings } from "../hooks/useHomeContent";
import { trackDesignView, trackWhatsApp } from "../lib/track";
import useReveal from "../hooks/useReveal";
import { relatedDesigns } from "../utils/relatedDesigns";

export default function DesignDetailPage() {
  const { catSlug, subSlug, number } = useParams();
  const navigate = useNavigate();
  const { getDesignByPartsMerged, allDesignsMerged } = useCatalog();
  const design = useMemo(
    () => getDesignByPartsMerged(catSlug, subSlug, number),
    [catSlug, subSlug, number, getDesignByPartsMerged]
  );

  const { isSaved, toggle, items: wishlistItems } = useWishlist();
  const store = useStoreSettings();
  const [activeMedia, setActiveMedia] = useState("photo-0"); // 'photo-N' | 'video'
  const [qty, setQty] = useState(1);
  const videoRef = useRef(null);
  const dragRef = useRef({ startX: 0, active: false });

  // Ordered list of media the user swipes through: photos first, then the video (if any).
  // Built with useMemo so it stays stable across renders; must be declared BEFORE any
  // early return to satisfy React's rules-of-hooks.
  const mediaList = useMemo(() => {
    if (!design) return [];
    const gal = design.gallery && design.gallery.length ? design.gallery : [design.image];
    const list = gal.map((src, i) => ({ type: "photo", src, key: `photo-${i}` }));
    if (design.video) list.push({ type: "video", src: design.video, key: "video" });
    return list;
  }, [design]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setActiveMedia("photo-0");
    if (design) trackDesignView(design);
  }, [catSlug, subSlug, number, design]);

  // Kick the video to play whenever the user swipes to it — iOS Safari
  // sometimes ignores autoplay for elements mounted after user interaction.
  useEffect(() => {
    const isVideo = mediaList.find((m) => m.key === activeMedia)?.type === "video";
    if (isVideo && videoRef.current) {
      const v = videoRef.current;
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
  }, [activeMedia, mediaList]);

  useReveal();

  const related = useMemo(
    () => (design ? relatedDesigns(design, wishlistItems, allDesignsMerged, 3) : []),
    [design, wishlistItems, allDesignsMerged]
  );

  if (!design) {
    return (
      <div className="alankar-site">
        <Header />
        <div className="wrap" style={{ padding: "140px 28px", textAlign: "center" }}>
          <h2>Design not found</h2>
          <p style={{ marginTop: 12 }}>
            <button className="btn btn-outline" onClick={() => navigate("/")}>
              <ArrowLeft size={16} /> Back to Home
            </button>
          </p>
        </div>
        <Footer />
      </div>
    );
  }

  const saved = isSaved(design.id);

  const gallery = design.gallery && design.gallery.length ? design.gallery : [design.image];

  const currentIdx = Math.max(
    0,
    mediaList.findIndex((m) => m.key === activeMedia)
  );
  const showingVideo = mediaList[currentIdx]?.type === "video";
  const activePhotoIdx = showingVideo ? 0 : currentIdx;

  const goTo = (i) => {
    if (i < 0 || i >= mediaList.length) return;
    setActiveMedia(mediaList[i].key);
  };
  const goNext = () => goTo(Math.min(currentIdx + 1, mediaList.length - 1));
  const goPrev = () => goTo(Math.max(currentIdx - 1, 0));

  const onPointerDown = (e) => {
    dragRef.current = { startX: e.clientX, active: true };
  };
  const onPointerUp = (e) => {
    if (!dragRef.current.active) return;
    const dx = e.clientX - dragRef.current.startX;
    dragRef.current.active = false;
    if (Math.abs(dx) < 40) return;             // ignore taps
    if (dx < 0) goNext(); else goPrev();
  };

  return (
    <div className="alankar-site">
      <Header />

      <div className="dp-wrap">
        <div className="wrap">
          <nav className="cat-crumbs dp-crumbs">
            <Link to="/">Home</Link>
            <ChevronRight size={13} />
            <Link to="/#collections">Collections</Link>
            <ChevronRight size={13} />
            <Link to={`/collections/${design.category.slug}`}>
              {design.category.label}
            </Link>
            <ChevronRight size={13} />
            <Link
              to={`/collections/${design.category.slug}/${design.subCategory.slug}`}
              data-testid="dp-crumb-sub"
            >
              {design.subCategory.label}
            </Link>
          </nav>

          <div className="dp-grid">
            {/* LEFT — media */}
            <div className="dp-media">
              <div
                className="dp-stage dp-stage--swipe"
                onPointerDown={onPointerDown}
                onPointerUp={onPointerUp}
                onPointerCancel={() => (dragRef.current.active = false)}
                data-testid="dp-stage"
                style={{ touchAction: "pan-y", cursor: "grab" }}
              >
                {showingVideo && design.video ? (
                  <video
                    ref={videoRef}
                    key={design.video}
                    src={design.video}
                    poster={design.image}
                    autoPlay
                    loop
                    muted
                    playsInline
                    // iOS Safari legacy attribute; keeps video inline on older devices
                    webkit-playsinline="true"
                    preload="metadata"
                    controls={false}
                    onClick={(e) => {
                      const v = e.currentTarget;
                      if (v.paused) v.play().catch(() => {});
                    }}
                  />
                ) : (
                  <img src={gallery[activePhotoIdx]} alt={design.name} />
                )}
              </div>
              <div className="dp-thumbs">
                {gallery.map((g, i) => (
                  <button
                    key={g + i}
                    type="button"
                    className={!showingVideo && activePhotoIdx === i ? "on" : ""}
                    onClick={() => setActiveMedia(`photo-${i}`)}
                    aria-label={`View photo ${i + 1}`}
                  >
                    <img src={g} alt="" />
                  </button>
                ))}
                {design.video && (
                  <button
                    type="button"
                    className={`dp-thumb-video ${showingVideo ? "on" : ""}`}
                    onClick={() => setActiveMedia("video")}
                    aria-label="Play video"
                  >
                    <img src={design.image} alt="" />
                    <span>
                      <Play size={14} fill="currentColor" strokeWidth={0} />
                    </span>
                  </button>
                )}
              </div>
            </div>

            {/* RIGHT — info */}
            <div className="dp-info">
              <span className="dp-eyebrow">
                {design.limitedCollectionId ? (
                  <>Limited Edition</>
                ) : (
                  <>{design.category.label} · {design.subCategory.label}</>
                )}
              </span>
              <h1 className="dp-title">{design.name}</h1>
              <p className="dp-sub">
                Ref: <b>{design.id.toUpperCase()}</b>
              </p>

              <div className="dp-actions">
                <span className="qty-wrap" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <span className="qty-label" data-testid="qty-label">Qty</span>
                  <span className="qty-stepper" data-testid="qty-stepper">
                    <button
                      type="button"
                      onClick={() => setQty((q) => Math.max(1, q - 1))}
                      disabled={qty <= 1}
                      aria-label="Decrease quantity"
                      data-testid="qty-minus"
                    >
                      −
                    </button>
                    <input
                      type="number"
                      min={1}
                      max={99}
                      value={qty}
                      onChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        setQty(Number.isFinite(n) ? Math.max(1, Math.min(99, n)) : 1);
                      }}
                      data-testid="qty-input"
                    />
                    <button
                      type="button"
                      onClick={() => setQty((q) => Math.min(99, q + 1))}
                      disabled={qty >= 99}
                      aria-label="Increase quantity"
                      data-testid="qty-plus"
                    >
                      +
                    </button>
                  </span>
                </span>
                <button
                  type="button"
                  className={`btn btn-outline dp-heart ${saved ? "saved" : ""}`}
                  onClick={() => toggle(design)}
                >
                  <Heart
                    size={16}
                    fill={saved ? "currentColor" : "none"}
                    strokeWidth={saved ? 0 : 1.6}
                  />
                  {saved ? "Saved to Wishlist" : "Add to Wishlist"}
                </button>
                <a
                  className="btn btn-primary"
                  target="_top"
                  rel="noreferrer"
                  href={store.waLink(`${design.name} — Qty: ${qty}`)}
                  onClick={() => trackWhatsApp({ design, source: "detail", quantity: qty })}
                  data-testid="dp-enquire"
                >
                  <MessageCircle size={16} /> Send Enquiry
                </a>
              </div>

              <div className="dp-specs">
                <div className="dp-spec">
                  <Scale size={16} />
                  <div>
                    <span className="lbl">Weight</span>
                    <span className="val">
                      {design.weight} {design.weightUnit}
                    </span>
                  </div>
                </div>
                <div className="dp-spec">
                  <Sparkles size={16} />
                  <div>
                    <span className="lbl">Purity</span>
                    <span className="val">{design.purity}</span>
                  </div>
                </div>
                <div className="dp-spec">
                  <Award size={16} />
                  <div>
                    <span className="lbl">Certification</span>
                    <span className="val">{design.hallmark}</span>
                  </div>
                </div>
                <div className="dp-spec">
                  <Tag size={16} />
                  <div>
                    <span className="lbl">Making Charge</span>
                    <span className="val">{design.makingCharge}</span>
                  </div>
                </div>
              </div>

              <div className="dp-desc">
                <h3>About this design</h3>
                {design.description ? (
                  <p>{design.description}</p>
                ) : (
                  <p>
                    Handpicked {design.styleName.toLowerCase()} from our{" "}
                    <strong>{design.subCategory.label}</strong> collection at
                    Alankar Jewellers, Rasta Peth. Every piece is hallmarked and
                    finished in our workshop for over 30 years — designed to be
                    worn every day and passed down for generations.
                  </p>
                )}
                <ul>
                  <li>{design.hallmark} — guaranteed purity</li>
                  <li>Free lifetime polishing at our Rasta Peth store</li>
                  <li>Old jewellery exchange available</li>
                  <li>Photos & video shown — visit for the real shine</li>
                </ul>
                <p className="dp-price-note">{design.priceNote}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* RELATED */}
      <section className="dp-related">
        <div className="wrap">
          <div className="dp-related-head reveal">
            <div>
              <span className="eyebrow">You may also like</span>
              <h2>More from {design.subCategory.label}</h2>
            </div>
            <Link
              to={`/collections/${design.category.slug}`}
              className="cat-block-cta"
            >
              View all {design.category.label} designs
            </Link>
          </div>
          <div className="design-grid">
            {related.map((d) => (
              <DesignCard
                key={d.id}
                design={d}
                onOpen={() => navigate(designUrl(d))}
              />
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
